import { useTranslation } from "src/context/Translations/context";
import React, {useState, useEffect} from "react";
import { RCCGrid, RCCHidden } from "react-common-components";
import { IRisksMatrixComponent } from "../../interface";
import * as Matrix from "./styles";
import { CheckImpact, CheckProbability, CheckQuadrant, IRisks } from "../../../Panel/interface";
import { IDictionaryMatrix, IMatrixCells, ISelectedCell } from "./interface";

const RisksMatrixComponent: React.FC<IRisksMatrixComponent> = ({
	handleClickCell,
	data
}) => { 

	const { translate } = useTranslation();

	const [matrixOpportunity, setMatrixOpportunity] = useState<IMatrixCells[] | null>();
	const [matrixThreat, setMatrixThreat] = useState<IMatrixCells[] | null>();
	const [selectedCell, setSelectedCell] = useState<ISelectedCell | undefined>(undefined);

	const impact = [
		{ value: CheckImpact.VERY_HIGH, label:  translate("Muito alto")},
		{ value: CheckImpact.HIGH, label:  translate("Alto")},
		{ value: CheckImpact.MODERATE, label:  translate("Moderado")},
		{ value: CheckImpact.LOW, label: translate("Baixo")}, 
		{ value: CheckImpact.VERY_LOW, label: translate("Muito baixo")} 
	];

	const probability = [
		{ value: CheckProbability.VERY_HIGH, label:  translate("Muito alto")},
		{ value: CheckProbability.HIGH, label:  translate("Alto")},
		{ value: CheckProbability.MODERATE, label:  translate("Moderado")},
		{ value: CheckProbability.LOW, label: translate("Baixo")}, 
		{ value: CheckProbability.VERY_LOW, label: translate("Muito baixo")} 
	];

	const organizedArrayOfCells = (dataMatrix: IRisks[], impactValue: IDictionaryMatrix[], probabilityValue: IDictionaryMatrix[]) : any => {
		const matrix: IMatrixCells[] = [];
		for(let i = 0; i < probabilityValue.length; i += 1){
			for(let j = 0; j < impactValue.length; j+= 1){
				const count = dataMatrix.filter((item: IRisks) => item.rib001Probabilidade === probability[i].value && item.rib001Impacto === impactValue[j].value);
				matrix.push({
					value: probabilityValue[i].value * impactValue[j].value,
					count: count.length,
					probability: probabilityValue[i].value,
					impact: impactValue[j].value
				});
			}
		}
		return matrix;
	};

	const choiceColor = (value: number) => {
		if(value >= 1800 ) return "var(--red-light)";
	 	if(value >= 600 ) return "var(--yellow-light)";
		return "var(--green-light)";
	};

	useEffect(() => {
		const opportunityData = data.filter((risk : IRisks) => risk.rib001Quadrante === CheckQuadrant.OPPORTUNITY);
		const organizedArrayOfCellsOpportunity = organizedArrayOfCells(opportunityData, impact, probability);
		setMatrixOpportunity(organizedArrayOfCellsOpportunity) ;
	
		const threatData = data.filter((risk : IRisks) => risk.rib001Quadrante === CheckQuadrant.THREAT);
		const organizedArrayOfCellsThreat = organizedArrayOfCells(threatData, impact.slice().reverse(), probability);
		setMatrixThreat(organizedArrayOfCellsThreat);
	}, [data]);

	useEffect(() => {
		handleClickCell(selectedCell?.probability, selectedCell?.impact, selectedCell?.quadrant );
	}, [selectedCell]);


	const handleClickSelect = (probabilityValue: CheckProbability, impactValue : CheckImpact, quadrantValue: CheckQuadrant) => {
		if(selectedCell?.probability === probabilityValue && selectedCell?.impact === impactValue && selectedCell.quadrant === quadrantValue){
			setSelectedCell(undefined);
			return;
		}
		setSelectedCell({
			probability: probabilityValue,
			impact: impactValue,
			quadrant: quadrantValue
		});
	};

	return (
		<>
			<RCCGrid
				container
				item
				styleInline="font-size: 0.83rem;"
			>
				<RCCGrid
					container
					item
				>
					{/* Ameaças */}
					<RCCGrid
						container
						item
						xs={12}
						sm={12}
						md={6}
						style={{ paddingTop: "1rem" }}
					>
						<Matrix.Title color="var(--red)">
							{translate("Ameaças")}
						</Matrix.Title>
						<RCCGrid
							item
							container
							xs={12}
							style={{ flexWrap: "wrap" }}
						>
							<Matrix.VerticalTitle >
								<span>{translate("Probabilidade")}</span>	
							</Matrix.VerticalTitle>

							<Matrix.WrapCellsDescriptionVertical>
								{probability.map((prob: IDictionaryMatrix) => (
									<Matrix.CellDescriptionVertical key={`risks-matrix-threat-probability-${prob.label}`}>
										{prob.label}
									</Matrix.CellDescriptionVertical>
								))}
							</Matrix.WrapCellsDescriptionVertical>

							<Matrix.WrapCellsMatrix>
								{matrixThreat && matrixThreat.map((cell : IMatrixCells) => (
									<Matrix.CellMatrix 
										key={`risks-matrix-threat-${cell.probability}-${cell.impact}`}
										background={choiceColor(cell.value)} 
										opacity={cell.count > 0 ? 1: 0.5} 
										active={selectedCell?.quadrant === CheckQuadrant.THREAT && selectedCell?.probability === cell.probability && selectedCell?.impact === cell.impact}
										onClick={() => { 
											handleClickSelect(cell.probability, cell.impact, CheckQuadrant.THREAT);
										}}>
										{cell.count}
									</Matrix.CellMatrix>
								))}	
							</Matrix.WrapCellsMatrix>

							<Matrix.Spacer />

							<Matrix.WrapCellsDescriptionHorizontal>
								{impact.slice().reverse().map((prob: any) => (
									<Matrix.CellDescriptionHorizontall key={`risks-matrix-threat-probability-horizontal${prob.label}`}>
										{`${prob.label}` }
									</Matrix.CellDescriptionHorizontall>
								))}
							</Matrix.WrapCellsDescriptionHorizontal>

							<RCCHidden mdUp>
								<Matrix.Spacer />
								<Matrix.TitleHorizontal spacer={1} color="var(--grey)">
									{translate("Impacto")}
								</Matrix.TitleHorizontal>
							</RCCHidden>

						</RCCGrid>

					</RCCGrid>

					{/* Oportunidades */}
					<RCCGrid
						container
						item
						xs={12}
						sm={12}
						md={6}
						style={{ paddingTop: "1rem"}}
					>
						<Matrix.Title color="var(--grey-darker)">	
							{translate("Oportunidades")}
						</Matrix.Title>
						<RCCGrid
							item
							container
							xs={12}
							style={{ flexWrap: "wrap" }}
						>

							<Matrix.WrapCellsMatrix>
								{matrixOpportunity && matrixOpportunity.map((cell : IMatrixCells) => (
									<Matrix.CellMatrix 
										key={`risks-matrix-opportunities-${cell.probability}-${cell.impact}`}
										background={choiceColor(cell.value)} 
										opacity={cell.count > 0 ? 1 : 0.5} 
										active={selectedCell?.quadrant === CheckQuadrant.OPPORTUNITY && selectedCell?.probability === cell.probability && selectedCell?.impact === cell.impact}
										onClick={() => {
											handleClickSelect(cell.probability, cell.impact, CheckQuadrant.OPPORTUNITY);
										}}
									>
										{cell.count}
									</Matrix.CellMatrix>
								))}		
							</Matrix.WrapCellsMatrix>
							
							<Matrix.WrapCellsDescriptionVertical>
								{probability.map((prob: any) => (
									<Matrix.CellDescriptionVertical 	key={`risks-matrix-opportunities-probability-${prob.label}`}>
										{prob.label}
									</Matrix.CellDescriptionVertical>
								))}
							</Matrix.WrapCellsDescriptionVertical>
									
							<Matrix.VerticalTitle>
								<span>{translate("Probabilidade")}</span>	
							</Matrix.VerticalTitle>

							<Matrix.WrapCellsDescriptionHorizontal>
								{impact.map((prob: any) => (
									<Matrix.CellDescriptionHorizontall key={`risks-matrix-opportunities-probability-horizontal-${prob.label}`}>
										{prob.label}
									</Matrix.CellDescriptionHorizontall>
								))}
							</Matrix.WrapCellsDescriptionHorizontal>

							<RCCHidden mdUp>
								<Matrix.Spacer />
								<Matrix.TitleHorizontal spacer={1} color="var(--grey)">
									{translate("Impacto")}
								</Matrix.TitleHorizontal>
								<Matrix.Spacer />
							</RCCHidden>
						</RCCGrid>
						
					</RCCGrid>

					<RCCHidden smDown>
						<Matrix.Spacer />
						<Matrix.TitleHorizontal spacer={2} color="var(--grey)">
							{translate("Impacto")}
						</Matrix.TitleHorizontal>
					</RCCHidden>

				</RCCGrid>
			</RCCGrid>		
		</>
	);

};

export default RisksMatrixComponent;
